/**
 * Firestore write helpers for ticket operations.
 * All functions write to the ticketChecks collection.
 */
import {
  doc, getDoc, getDocFromCache, serverTimestamp, setDoc, addDoc, collection, runTransaction,
} from 'firebase/firestore';
import { db } from '../../firebase';
import { isLiveTicket } from './lib/liveTickets';
import { normalizeTimeMap, getPrepSummary, getPrepTrackingPatch } from './ticketUtils';
import { computeStampedFireTimes } from './ticketTimingUtils';
import { isSlotFree } from './kdsLoadAlerts';
import { NOTIFY_API_BASE } from './ticketConstants';
import { logTicketEvent, timingSnapshot, TICKET_EVENTS } from './lib/ticketEvents';

/**
 * Read a ticket's latest state before writing. When an open board keeps this
 * ticket live (useTicketChecks → lib/liveTickets), the local cache already holds
 * exactly what the server last pushed plus our pending writes, so read it there:
 * instant and free. Otherwise — or if it isn't cached — ask the server, as before.
 * (A per-ticket listener used to make getDoc() free; the batched listeners don't.)
 */
async function readLatest(ref) {
  if (isLiveTicket(ref.id)) {
    try { return await getDocFromCache(ref); } catch { /* not cached — fall through */ }
  }
  return getDoc(ref);
}

// ─── Toggle a single item's checked state ────────────────────────────────────

export async function sqFsToggleItem(orderId, itemIdx, wasChecked, currentChecked, items, createdAtISO, currentItemCheckedMs, currentPrepData) {
  const ref = doc(db, 'ticketChecks', orderId);
  const prev = Array.isArray(currentChecked) ? currentChecked : [];
  const next = wasChecked ? prev.filter(i => i !== itemIdx) : [...new Set([...prev, itemIdx])];
  const allDone = next.length >= (items ? items.length : 0) && (items ? items.length : 0) > 0;
  // Read Firestore state when completing (need latest prep data + reopenedAt)
  const latest = (allDone || getPrepSummary(currentPrepData).source === 'completion')
    ? (await readLatest(ref)).data() || {}
    : {};
  // For reopened tickets, measure time from reopenedAt, not original createdAt
  let startMs = createdAtISO ? new Date(createdAtISO).getTime() : null;
  if (allDone && latest.reopenedForNewItems && latest.reopenedAt) {
    const reopenedDate = latest.reopenedAt.toDate ? latest.reopenedAt.toDate() : new Date(latest.reopenedAt);
    startMs = reopenedDate.getTime();
  }
  const mins = startMs ? Math.round((Date.now() - startMs) / 60000) : null;
  const latestPrep = getPrepSummary(latest);
  const incomingPrep = getPrepSummary(currentPrepData);
  const effectivePrep = latestPrep.timeMin != null ? latestPrep : incomingPrep;

  const prevMs = {
    ...normalizeTimeMap(currentItemCheckedMs),
    ...normalizeTimeMap(latest?.itemCheckedMs),
  };
  if (wasChecked) delete prevMs[itemIdx]; else prevMs[itemIdx] = Date.now();

  const patch = {
    checkedItems: next,
    itemCheckedMs: prevMs,
    completedAt: allDone ? serverTimestamp() : null,
    timeTakenMin: allDone ? mins : null,
    updatedAt: serverTimestamp(),
  };
  if (allDone) {
    Object.assign(patch, getPrepTrackingPatch(effectivePrep, mins, serverTimestamp()));
    // Clear reopen state on completion
    patch.reopenedForNewItems = false;
    patch.reopenedAt = null;
  } else if (effectivePrep.source === 'completion' && effectivePrep.timeMin != null) {
    patch.prepCompletedAt = null;
    patch.prepTimeMin = null;
    patch.prepSource = null;
  }
  await setDoc(ref, patch, { merge: true });

  // Audit trail — appended after the real write so it can never delay or fail it.
  logTicketEvent(orderId, wasChecked ? TICKET_EVENTS.ITEM_UNCHECKED : TICKET_EVENTS.ITEM_CHECKED, {
    itemIdx, checkedCount: next.length, itemCount: items ? items.length : 0,
  });
  if (allDone) {
    logTicketEvent(orderId, TICKET_EVENTS.COMPLETED, {
      timeTakenMin: mins, prepTimeMin: effectivePrep.timeMin ?? null,
      prepSource: effectivePrep.source ?? 'completion', via: 'lastItemChecked',
    });
  }
}

// ─── Assign / unassign all items ─────────────────────────────────────────────

export async function sqFsAssign(orderId, assigned, itemCount = 0) {
  const allIdx = assigned ? Array.from({ length: itemCount }, (_, i) => i) : [];
  await setDoc(doc(db, 'ticketChecks', orderId), {
    assigned: assigned && allIdx.length > 0,
    assignedAt: assigned ? serverTimestamp() : null,
    assignedItems: allIdx,
    updatedAt: serverTimestamp(),
  }, { merge: true });

  logTicketEvent(orderId, assigned ? TICKET_EVENTS.ASSIGNED : TICKET_EVENTS.UNASSIGNED, {
    itemCount, scope: 'all',
  });
}

// ─── Toggle a single item's assignment ───────────────────────────────────────

export async function sqFsToggleItemAssign(orderId, idx, currentAssigned) {
  const prev = Array.isArray(currentAssigned) ? currentAssigned : [];
  const next = prev.includes(idx) ? prev.filter(i => i !== idx) : [...prev, idx];
  await setDoc(doc(db, 'ticketChecks', orderId), {
    assigned: next.length > 0,
    assignedAt: next.length > 0 ? serverTimestamp() : null,
    assignedItems: next,
    updatedAt: serverTimestamp(),
  }, { merge: true });
}

// ─── Toggle chef-done for a single item ──────────────────────────────────────

export async function sqFsToggleChefDoneItem(orderId, idx, currentChefDone, currentAssigned, items, createdAtISO, currentChefDoneMs) {
  const prev = Array.isArray(currentChefDone) ? currentChefDone : [];
  const next = prev.includes(idx) ? prev.filter(i => i !== idx) : [...prev, idx];
  const prevAssigned = Array.isArray(currentAssigned) ? currentAssigned : [];
  const nextAssigned = prev.includes(idx)
    ? prevAssigned
    : (prevAssigned.includes(idx) ? prevAssigned : [...prevAssigned, idx]);
  const nextChefDoneMs = { ...normalizeTimeMap(currentChefDoneMs) };
  if (prev.includes(idx)) delete nextChefDoneMs[idx];
  else nextChefDoneMs[idx] = Date.now();
  const prepDone = Array.isArray(items) && items.length > 0 && next.length >= items.length;
  const prepMins = createdAtISO ? Math.round((Date.now() - new Date(createdAtISO).getTime()) / 60000) : null;
  const patch = {
    assigned: nextAssigned.length > 0,
    assignedAt: nextAssigned.length > 0 ? serverTimestamp() : null,
    assignedItems: nextAssigned,
    chefDoneItems: next,
    chefDoneMs: nextChefDoneMs,
    updatedAt: serverTimestamp(),
  };
  if (prepDone) {
    patch.prepCompletedAt = serverTimestamp();
    patch.prepTimeMin = prepMins;
    patch.prepSource = 'chef';
  } else {
    patch.prepCompletedAt = null;
    patch.prepTimeMin = null;
    patch.prepSource = null;
  }
  await setDoc(doc(db, 'ticketChecks', orderId), patch, { merge: true });

  logTicketEvent(orderId, prev.includes(idx) ? TICKET_EVENTS.ITEM_CHEF_UNDONE : TICKET_EVENTS.ITEM_CHEF_DONE, {
    itemIdx: idx, chefDoneCount: next.length, itemCount: Array.isArray(items) ? items.length : 0,
    prepTimeMin: prepDone ? prepMins : null,
  });
}

// ─── Mark ticket complete (all items done) ───────────────────────────────────

export async function sqFsComplete(orderId, items, createdAtISO, ticketName, total, existingItemCheckedMs, currentPrepData) {
  const nowMs = Date.now();
  const ref = doc(db, 'ticketChecks', orderId);
  const latest = (await readLatest(ref)).data() || {};
  const latestPrep = getPrepSummary(latest);
  const incomingPrep = getPrepSummary(currentPrepData);
  const effectivePrep = latestPrep.timeMin != null ? latestPrep : incomingPrep;

  // For reopened tickets, calculate time from reopenedAt (the new-items cycle),
  // not the original createdAt which could be hours earlier.
  let startMs = createdAtISO ? new Date(createdAtISO).getTime() : null;
  if (latest.reopenedForNewItems && latest.reopenedAt) {
    const reopenedDate = latest.reopenedAt.toDate ? latest.reopenedAt.toDate() : new Date(latest.reopenedAt);
    startMs = reopenedDate.getTime();
  }
  const mins = startMs ? Math.round((nowMs - startMs) / 60000) : null;

  const prev = {
    ...normalizeTimeMap(existingItemCheckedMs),
    ...normalizeTimeMap(latest?.itemCheckedMs),
  };
  const itemCheckedMs = Object.fromEntries(items.map((_, i) => [i, prev[i] ?? nowMs]));

  const patch = {
    checkedItems: items.map((_, i) => i),
    itemCheckedMs,
    manuallyCompleted: true,
    completedAt: serverTimestamp(),
    timeTakenMin: mins,
    updatedAt: serverTimestamp(),
    ticketName: ticketName || null,
    itemsSnapshot: items.map(it => ({
      name: it.name,
      qty: it.qty,
      priceCents: it.priceCents,
      variationName: it.variationName || null,
      note: it.note || null,
      modifiers: Array.isArray(it.modifiers) ? it.modifiers : [],
    })),
    total: total ?? null,
    createdAtISO: createdAtISO || null,
    // Clear reopen state on completion
    reopenedForNewItems: false,
    reopenedAt: null,
    ...getPrepTrackingPatch(effectivePrep, mins, serverTimestamp()),
  };
  await setDoc(ref, patch, { merge: true });

  logTicketEvent(orderId, TICKET_EVENTS.COMPLETED, {
    timeTakenMin: mins, prepTimeMin: effectivePrep.timeMin ?? null,
    prepSource: effectivePrep.source ?? 'completion',
    itemCount: items.length, ticketName: ticketName || null,
    totalCents: total ?? null, via: 'manualComplete',
  });
}

// ─── Reopen a completed ticket ───────────────────────────────────────────────

export async function sqFsReopen(orderId) {
  const ref = doc(db, 'ticketChecks', orderId);
  // Read the timing facts BEFORE the write nulls them — this snapshot is the
  // only surviving record of how long the order originally took.
  let before = {};
  try { before = (await readLatest(ref)).data() || {}; } catch { before = {}; }

  await setDoc(doc(db, 'ticketChecks', orderId), {
    checkedItems: [], itemCheckedMs: {}, manuallyCompleted: false, assigned: false,
    assignedItems: [], chefDoneItems: [], chefDoneMs: {},
    completedAt: null, assignedAt: null, timeTakenMin: null, updatedAt: serverTimestamp(),
    prepCompletedAt: null, prepTimeMin: null, prepSource: null,
    reopenedForNewItems: false, reopenedAt: null,
    sentToFrontItems: [],
  }, { merge: true });

  logTicketEvent(orderId, TICKET_EVENTS.REOPENED, timingSnapshot(before));
}

// ─── Stamp fire times for items added to an ACTIVE (non-completed) ticket ─────

// Records a fire time for every item index we haven't seen before, so items
// added mid-service start their own clock instead of inheriting the order's
// original createdAt. The completed → reopen flow (sqFsReopenForNewItems) owns
// fire times for completed tickets, so this no-ops on those.
export async function sqFsStampItemFireTimes(orderId, items, createdAtISO) {
  const ref = doc(db, 'ticketChecks', orderId);
  const latest = (await readLatest(ref)).data() || {};
  if (latest.manuallyCompleted) return; // reopen flow handles completed tickets
  const merged = computeStampedFireTimes({
    storedFireTimes: latest.itemFireTimes || null,
    itemCount: Array.isArray(items) ? items.length : 0,
    createdAtMs: createdAtISO ? new Date(createdAtISO).getTime() : Date.now(),
    nowMs: Date.now(),
  });
  if (!merged) return; // nothing new to record
  await setDoc(ref, { itemFireTimes: merged, updatedAt: serverTimestamp() }, { merge: true });
}

// ─── Reopen for new items (preserves old checked state) ──────────────────────

export async function sqFsReopenForNewItems(orderId, prevCheckedItems, currentItems, createdAtISO) {
  const ref = doc(db, 'ticketChecks', orderId);
  const latest = (await readLatest(ref)).data() || {};
  const newSnapshot = Array.isArray(currentItems) ? currentItems.map(it => ({
    name: it.name,
    qty: it.qty,
    priceCents: it.priceCents,
    variationName: it.variationName || null,
    note: it.note || null,
    modifiers: Array.isArray(it.modifiers) ? it.modifiers : [],
  })) : undefined;

  // Build itemFireTimes: preserve originals, stamp new items with NOW
  const nowMs = Date.now();
  const prevFireTimes = latest.itemFireTimes || {};
  const snapshotCount = latest.itemsSnapshot ? latest.itemsSnapshot.length : (prevCheckedItems || []).length;
  const createdMs = createdAtISO ? new Date(createdAtISO).getTime() : nowMs;
  const itemFireTimes = {};
  const totalItems = currentItems ? currentItems.length : 0;
  for (let i = 0; i < totalItems; i++) {
    if (prevFireTimes[i] != null) {
      itemFireTimes[i] = prevFireTimes[i]; // preserve
    } else if (i < snapshotCount) {
      itemFireTimes[i] = createdMs; // original item
    } else {
      itemFireTimes[i] = nowMs; // NEW item — fire time = now
    }
  }

  await setDoc(ref, {
    manuallyCompleted: false,
    completedAt: null,
    timeTakenMin: null,
    checkedItems: prevCheckedItems,
    ...(newSnapshot ? { itemsSnapshot: newSnapshot } : {}),
    prepCompletedAt: null,
    prepTimeMin: null,
    prepSource: null,
    reopenedForNewItems: true,
    reopenedAt: serverTimestamp(),
    itemFireTimes,
    updatedAt: serverTimestamp(),
  }, { merge: true });

  logTicketEvent(orderId, TICKET_EVENTS.REOPENED_NEW_ITEMS, {
    ...timingSnapshot(latest),
    prevItemCount: snapshotCount,
    newItemCount: totalItems,
    addedItems: Math.max(0, totalItems - snapshotCount),
  });
}

// ─── Claim a cross-device alert slot (dedup load alerts) ─────────────────────

// Atomically claims a shared slot so that when multiple tabs/devices each detect
// the same kitchen-load alert, only ONE actually sends. Returns true if THIS
// caller won the slot (should send), false if another device already holds it.
// Fails OPEN on error — better a rare duplicate than a missed "kitchen behind".
export async function claimAlertSlot(dedupKey, windowMs) {
  const ref = doc(db, 'kdsAlertDedup', dedupKey);
  try {
    return await runTransaction(db, async (tx) => {
      const snap = await tx.get(ref);
      const nowMs = Date.now();
      const prev = snap.exists() ? (snap.data().claimedAtMs || null) : null;
      if (!isSlotFree(prev, nowMs, windowMs)) return false;
      tx.set(ref, { claimedAtMs: nowMs, expiresAt: nowMs + windowMs, updatedAt: serverTimestamp() });
      return true;
    });
  } catch {
    return true; // fail-open: don't lose an alert because dedup hiccupped
  }
}

// ─── Set pending reason on a ticket ──────────────────────────────────────────

export async function sqFsSetPendingReason(orderId, reason) {
  await setDoc(doc(db, 'ticketChecks', orderId), {
    pendingReason: reason || null,
    pendingReasonAt: reason ? serverTimestamp() : null,
    updatedAt: serverTimestamp(),
  }, { merge: true });
}

// ─── Request Help (SMS + FCM + Firestore) ────────────────────────────────────

export async function sqFsRequestHelp({ orderId, ticketName, employeeName, message }) {
  const name = employeeName || 'Staff';
  const tname = ticketName || orderId;
  const msg = message || '';

  // 1. Fire notification immediately — never blocked by Firestore
  fetch(NOTIFY_API_BASE + '/api/send-notification', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      alertKey: 'help_' + orderId + '_' + Math.floor(Date.now() / 60000),
      title: name + ' needs help!',
      body: 'Ticket: ' + tname + (msg ? ' - ' + msg : ''),
      severity: 'critical',
      channels: ['telegram', 'ntfy', 'expo', 'sms', 'fcm'],
      fcmTokens: [],
    }),
  }).catch(() => {});

  // 2. Write to Firestore for owner dashboard live view
  try {
    const ref = await addDoc(collection(db, 'helpRequests'), {
      orderId,
      ticketName: tname,
      employeeName: name,
      message: msg || 'Employee needs help',
      status: 'open',
      createdAt: serverTimestamp(),
      resolvedAt: null,
    });
    return ref.id;
  } catch (e) {
    console.error('[SOS] Firestore failed (non-fatal):', e.message);
    return null;
  }
}

// ─── Set quality flags / note on a ticket ────────────────────────────────────

export async function sqFsSetQuality(orderId, flags, note, by) {
  const cleanFlags = Array.isArray(flags) ? flags.filter(Boolean) : [];
  const hasAny = cleanFlags.length > 0 || (note && note.trim());
  await setDoc(doc(db, 'ticketChecks', orderId), {
    quality: hasAny ? {
      flags: cleanFlags,
      note: (note || '').trim() || null,
      by: by || null,
      taggedAtMs: Date.now(),
    } : null,
    qualityUpdatedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  }, { merge: true });

  logTicketEvent(orderId, TICKET_EVENTS.QUALITY_TAGGED, {
    flags: cleanFlags, cleared: !hasAny, by: by || null,
  });
}

// ─── Save chef profiles to Firestore ─────────────────────────────────────────

export async function saveChefProfiles(profiles) {
  try {
    await setDoc(doc(db, 'kdsConfig', 'chefProfiles'), {
      profiles,
      updatedAt: serverTimestamp(),
    }, { merge: true });
  } catch (e) {
    console.error('saveChefProfiles:', e);
  }
}
